import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, CheckCircle2 } from 'lucide-react';

interface CategoryFormProps {
  category: string;
  isOpen: boolean;
  onClose: () => void;
  selectedSymptoms?: string[];
  onSymptomToggle?: (symptom: string) => void;
}

const EMOTIONAL_SYMPTOMS = [
  {
    text: "Je ressens une anxiété diffuse",
    description: "Une tension sourde dans le corps, comme une pression dans la poitrine ou le ventre, sans raison apparente, accompagnée d'une légère agitation intérieure.",
    intensity: "orange"
  },
  {
    text: "Je me sens en surcharge émotionnelle",
    description: "Montée brutale de panique, cœur qui s'emballe, souffle court, sensation de mourir ou de perdre le contrôle.",
    intensity: "red"
  },
  {
    text: "Je ressens une hypersensibilité aux émotions des autres",
    description: "Impression qu'il faut faire quelque chose tout de suite sans savoir quoi, comme si quelque chose allait mal arriver.",
    intensity: "orange"
  },
  {
    text: "Je me sens incompris(e) ou invisible",
    description: "Tout t'agace, même des choses banales ; les bruits, les gens, les imprévus déclenchent une colère intérieure difficile à calmer.",
    intensity: "orange"
  },
  {
    text: "Je ressens une fatigue émotionnelle profonde",
    description: "Passer de l'euphorie à la tristesse ou de la paix à la rage en quelques minutes, sans comprendre pourquoi.",
    intensity: "orange"
  },
  {
    text: "Je ressens des sauts d'humeur brusques",
    description: "Passer de l'euphorie à la tristesse ou de la paix à la rage en quelques minutes, sans comprendre pourquoi.",
    intensity: "red"
  },
  {
    text: "Je ressens un sentiment de vide ou d'absurdité",
    description: "Comme si rien n'avait vraiment de sens, même ce qui t'importait avant ; une sensation d'éloignement de la vie.",
    intensity: "red"
  },
  {
    text: "Je me sens à côté de moi-même",
    description: "Comme si tu jouais un rôle dans ta propre vie, ou que tu regardais quelqu'un d'autre vivre à ta place.",
    intensity: "red"
  },
  {
    text: "Je ressens une hypervigilance",
    description: "Sensation constante de danger, corps en alerte, oreilles qui guettent, tension musculaire continue, difficulté à se détendre.",
    intensity: "red"
  },
  {
    text: "J'ai des pensées intrusives",
    description: "Images, idées ou envies soudaines, parfois choquantes ou incompréhensibles, qui arrivent sans prévenir.",
    intensity: "red"
  },
  {
    text: "Je ressens une peur sans objet précis",
    description: "Une inquiétude présente sans savoir pourquoi, comme une petite alarme interne qui ne s'éteint pas.",
    intensity: "red"
  },
  {
    text: "Je me sens déconnecté(e) des autres",
    description: "Comme si tu étais sur une autre fréquence, que tu comprenais les gens mais qu'ils ne te voyaient pas vraiment.",
    intensity: "red"
  },
  {
    text: "Je ressens une mélancolie constante",
    description: "Tristesse douce et continue, pas violente mais toujours là, comme un voile gris sur tout.",
    intensity: "red"
  },
  {
    text: "Je ressens une surcharge mentale",
    description: "Trop de pensées, trop de tâches, tout semble demander trop d'énergie, même le simple fait de penser.",
    intensity: "red"
  },
  {
    text: "Je ressens des ruminations mentales",
    description: "Pensées qui tournent en boucle, même si tu veux les arrêter ; dialogues intérieurs sans fin, souvent négatifs.",
    intensity: "orange"
  }
];

export function CategoryForm({ category, isOpen, onClose, selectedSymptoms = [], onSymptomToggle }: CategoryFormProps) {
  if (category !== 'emotionnel') {
    return null;
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
          className="w-full overflow-hidden"
        >
          <Card className="bg-pink-50 border-pink-200 border-2">
            <CardContent className="p-6">
              <div className="flex items-start gap-4 mb-6">
                <div className="p-3 bg-white rounded-xl shadow-sm">
                  <Heart className="w-6 h-6 text-pink-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2">Ressenti Émotionnel</h3>
                  <p className="text-gray-600">
                    Cette section t'aide à identifier les signes d'un déséquilibre émotionnel ou psychologique. 
                    Elle explore des ressentis tels que l'agitation, la tristesse, l'irritabilité, la surcharge mentale, 
                    ou encore la perte de sens. Une clé pour écouter ton paysage intérieur et retrouver ton axe.
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <p className="text-sm text-gray-500 italic">
                  Sélectionne les ressentis qui correspondent à ton état actuel...
                </p>
                <div className="grid grid-cols-1 gap-3">
                  {EMOTIONAL_SYMPTOMS.map((symptom, index) => (
                    <label 
                      key={index}
                      className={`
                        flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors
                        ${selectedSymptoms?.includes(symptom.text)
                          ? symptom.intensity === 'red' 
                            ? 'bg-red-50 border-red-300'
                            : 'bg-orange-50 border-orange-300'
                          : 'bg-white hover:bg-gray-50 border-gray-200'}
                      `}
                      onClick={() => onSymptomToggle?.(symptom.text)}
                    >
                      <div className={`
                        w-5 h-5 rounded-full border-2 flex items-center justify-center
                        ${selectedSymptoms?.includes(symptom.text)
                          ? symptom.intensity === 'red'
                            ? 'border-red-500 bg-red-500'
                            : 'border-orange-500 bg-orange-500'
                          : 'border-gray-300'}
                      `}>
                        {selectedSymptoms?.includes(symptom.text) && (
                          <CheckCircle2 className="w-4 h-4 text-white" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium">{symptom.text}</div>
                        <div className="text-sm text-gray-600 mt-1">
                          {symptom.description}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </AnimatePresence>
  );
}